import React from "react";
import { eur, formatujPercentoBezZnamienkaText } from "../../utils/formatovanie";

function cislo(hodnota) {
  const vysledok = Number(hodnota);
  return Number.isFinite(vysledok) ? vysledok : 0;
}

function formatujSumu(hodnota) {
  return `${eur(Math.round(cislo(hodnota)))} €`;
}

function formatujPercento(hodnota) {
  return formatujPercentoBezZnamienkaText(cislo(hodnota));
}

function vytvorSparkline({ scenar, maxKonecnaHodnota, porovnajScenare }) {
  const sirka = 76;
  const vyska = 44;
  const odsX = 5;
  const odsY = 5;
  const pocetBodov = 10;

  const jednorazovo = Math.max(0, cislo(scenar?.jednorazovyVklad));
  const mesacne = Math.max(0, cislo(scenar?.mesacnyVklad));
  const horizontMesiace = Math.max(1, Math.round(Math.max(0, cislo(scenar?.horizont)) * 12));
  const rocneZhodnotenie = Math.max(0, cislo(scenar?.zhodnotenie)) / 100;
  const mesacnaMiera = Math.pow(1 + rocneZhodnotenie, 1 / 12) - 1;

  const vypocitajHodnotuVMesiaci = (mesiac) => {
    const faktor = Math.pow(1 + mesacnaMiera, mesiac);
    const hodnotaJednorazovo = jednorazovo * faktor;

    const hodnotaMesacnychVkladov = mesacne > 0
      ? (
          mesacnaMiera > 0
            ? mesacne * ((faktor - 1) / mesacnaMiera)
            : mesacne * mesiac
        )
      : 0;

    return Math.max(0, hodnotaJednorazovo + hodnotaMesacnychVkladov);
  };

  const hodnoty = Array.from({ length: pocetBodov }, (_, index) => {
    const pomer = index / (pocetBodov - 1);
    const mesiac = Math.round(horizontMesiace * pomer);

    return vypocitajHodnotuVMesiaci(mesiac);
  });

  const vlastnaKonecnaHodnota = Math.max(
    1,
    cislo(scenar?.vysledok?.konecnaHodnota),
    hodnoty[hodnoty.length - 1]
  );

  const vizualnyStrop = porovnajScenare
    ? Math.max(1, cislo(maxKonecnaHodnota), vlastnaKonecnaHodnota)
    : vlastnaKonecnaHodnota;

  const rozostupX = (sirka - (odsX * 2)) / (pocetBodov - 1);

  const body = hodnoty.map((hodnota, index) => {
    const pomerHodnoty = Math.min(1, Math.max(0, hodnota / vizualnyStrop));
    const x = odsX + (rozostupX * index);
    const y = (vyska - odsY) - (pomerHodnoty * (vyska - (odsY * 2)));

    return {
      x: Number(x.toFixed(2)),
      y: Number(y.toFixed(2)),
    };
  });

  const polyline = body.map((bod) => `${bod.x},${bod.y}`).join(" ");
  const area = [
    `M ${body[0].x} ${vyska - odsY}`,
    ...body.map((bod) => `L ${bod.x} ${bod.y}`),
    `L ${body[body.length - 1].x} ${vyska - odsY}`,
    "Z",
  ].join(" ");

  return { sirka, vyska, body, polyline, area };
}

function ziskajVizualScenara(scenar) {
  const konecnaHodnota = Math.max(0, cislo(scenar?.vysledok?.konecnaHodnota));
  const vklady = Math.max(0, cislo(scenar?.vysledok?.celkoveVklady));
  const vynosZDat = cislo(scenar?.vysledok?.celkovyVynos);
  const vynos = Math.max(0, vynosZDat || (konecnaHodnota - vklady));
  const sucet = vklady + vynos;

  return {
    konecnaHodnota,
    vklady,
    vynos,
    uholVkladov: sucet > 0 ? `${((vklady / sucet) * 360).toFixed(2)}deg` : "0deg",
  };
}

function ziskajTrieduTonu(index) {
  return `invest-scenar-vystup-card--tone-${(index % 3) + 1}`;
}

export default function InvesticneScenareVystupKarta({
  investScenareOpen,
  vysledkyScenarov,
  porovnajScenare,
}) {
  const najlepsiScenarId = React.useMemo(() => {
    if (!porovnajScenare || !Array.isArray(vysledkyScenarov) || !vysledkyScenarov.length) {
      return null;
    }

    return vysledkyScenarov.reduce((najlepsi, aktualny) => {
      const aktualnaHodnota = cislo(aktualny?.vysledok?.konecnaHodnota);
      const najlepsiaHodnota = cislo(najlepsi?.vysledok?.konecnaHodnota);

      return aktualnaHodnota > najlepsiaHodnota ? aktualny : najlepsi;
    }, vysledkyScenarov[0])?.id || null;
  }, [porovnajScenare, vysledkyScenarov]);

  const maxKonecnaHodnotaScenarov = React.useMemo(() => {
    if (!Array.isArray(vysledkyScenarov) || !vysledkyScenarov.length) {
      return 1;
    }

    return Math.max(
      1,
      ...vysledkyScenarov.map((scenar) => cislo(scenar?.vysledok?.konecnaHodnota))
    );
  }, [vysledkyScenarov]);

  if (!investScenareOpen) return null;

  return (
    <div className="card-section card-section--invest-scenare card-section--invest-scenare-vystup">
      <h2>Výstup scenárov</h2>

      <div className={`invest-scenare-vystup-grid ${porovnajScenare ? "invest-scenare-vystup-grid--compare" : "invest-scenare-vystup-grid--goals"}`}>
        {vysledkyScenarov.map((scenar, index) => {
          const sparkline = vytvorSparkline({
            scenar,
            maxKonecnaHodnota: maxKonecnaHodnotaScenarov,
            porovnajScenare,
          });
          const poslednyBod = sparkline.body[sparkline.body.length - 1];
          const { konecnaHodnota, vklady, vynos, uholVkladov } = ziskajVizualScenara(scenar);
          const jeZvyrazneny = porovnajScenare && scenar.id === najlepsiScenarId;

          return (
            <div
              key={scenar.id}
              className={[
                "invest-scenar-vystup-card",
                ziskajTrieduTonu(index),
                porovnajScenare ? "is-compare-mode" : "is-goal-mode",
                jeZvyrazneny ? "is-highlighted" : "",
              ].filter(Boolean).join(" ")}
            >
              <div className="invest-scenar-vystup-head">
                <div className="invest-scenar-vystup-title">{scenar.nazov || "Scenár"}</div>
                <div className="invest-scenar-vystup-meta">
                  {Math.max(0, cislo(scenar.horizont))} r • {formatujPercento(scenar.zhodnotenie)} % p.a.
                </div>
              </div>

              <div className="invest-scenar-vystup-value">
                {formatujSumu(konecnaHodnota)}
              </div>

              <div className="invest-scenar-visual">
                <div
                  className="invest-scenar-donut"
                  style={{ "--invest-scenar-vklady-angle": uholVkladov }}
                >
                  <div className="invest-scenar-donut__inner">
                    <svg
                      className="invest-scenar-sparkline"
                      viewBox={`0 0 ${sparkline.sirka} ${sparkline.vyska}`}
                      aria-hidden="true"
                    >
                      <path
                        d={sparkline.area}
                        className="invest-scenar-sparkline__area"
                      />
                      <polyline
                        points={sparkline.polyline}
                        className="invest-scenar-sparkline__line"
                        fill="none"
                      />
                      <circle
                        cx={poslednyBod.x}
                        cy={poslednyBod.y}
                        r="2.5"
                        className="invest-scenar-sparkline__end"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="invest-scenar-metriky">
                <div className="invest-scenar-metrika">
                  <span className="invest-scenar-metrika__label">
                    <span className="invest-scenar-metrika__dot invest-scenar-metrika__dot--vklady" />
                    Vklady
                  </span>
                  <strong>{formatujSumu(vklady)}</strong>
                </div>

                <div className="invest-scenar-metrika">
                  <span className="invest-scenar-metrika__label">
                    <span className="invest-scenar-metrika__dot invest-scenar-metrika__dot--vynos" />
                    Výnos
                  </span>
                  <strong>{formatujSumu(vynos)}</strong>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}